import { Loader } from 'src/design/atoms/Loader';
import { Select } from 'src/design/atoms/Select';
import React from 'react';
import type { UserSearchDto } from 'src/api/FiretrolApiClient';
import { useUserSearchOptions } from 'src/api/queries/users/useUserSearchOptions';
import useDebounce from 'src/hooks/useDebounce';

export interface ISubmittedBySelectorProps {
  value: number | undefined;
  onChange: (user: UserSearchDto | undefined) => void;
  label?: string;
  /** Seeds the label for the current value so an already-selected user renders before any search runs. */
  initialUser?: UserSearchDto | undefined;
  disabled?: boolean;
}

const userLabel = (user: UserSearchDto): string =>
  user.primaryOfficeName
    ? `${user.displayName} (${user.primaryOfficeName})`
    : user.displayName;

/** @registry Server-searched Select for choosing the user a Change was submitted by. */
export const SubmittedBySelector: React.FC<ISubmittedBySelectorProps> = ({
  value,
  onChange,
  label,
  initialUser,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = React.useState('');

  const selectedLabelRef = React.useRef<string | undefined>(undefined);

  // Re-typing the label of the option that is already selected is not a new
  // search, so the previous term stays in effect.
  const [searchTerm, setSearchTerm] = React.useState('');
  React.useEffect(() => {
    if (searchValue !== '' && searchValue === selectedLabelRef.current) return;
    setSearchTerm(searchValue);
  }, [searchValue]);
  const debouncedTerm = useDebounce(searchTerm, 300);
  const { data: found, isFetching: loading } =
    useUserSearchOptions(debouncedTerm);

  const results = React.useMemo(() => {
    const matches = found ?? [];
    const seed =
      initialUser && !matches.some((u) => u.id === initialUser.id)
        ? [initialUser]
        : [];
    return [...seed, ...matches];
  }, [found, initialUser]);

  const selected =
    value != null ? results.find((u) => u.id === value) : undefined;
  selectedLabelRef.current = selected ? userLabel(selected) : undefined;

  const data = React.useMemo(
    () =>
      results.map((user) => ({
        value: String(user.id),
        label: userLabel(user),
      })),
    [results],
  );

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      onChange(results.find((u) => String(u.id) === selectedValue));
    } else {
      onChange(undefined);
    }
  };

  return (
    <Select
      label={label}
      placeholder="Search users..."
      data={data}
      value={value != null ? String(value) : null}
      onChange={handleChange}
      disabled={disabled}
      onSearchChange={setSearchValue}
      searchable
      clearable
      rightSection={loading ? <Loader color="blue" size="xs" /> : undefined}
      nothingFoundMessage={loading ? 'Loading...' : 'No users found'}
    />
  );
};
